package terraultima

import kotlin.math.roundToInt
import kotlin.random.Random

object Setup {
    private val NAMES = listOf(
        "Adeyemi", "Novak", "Haas", "Okonkwo", "Lindqvist", "Bhatt", "Moreau", "Tanaka", "Reyes",
        "Kowalczyk", "Farouk", "Nakamura", "Oyelaran", "Ivanova", "Sato", "Duarte", "Mbeki",
        "Halvorsen", "Petrov", "Chaudhary", "Ferreira", "Yilmaz", "Nguyen", "Osei", "Lindgren"
    )

    fun begin(s: WorldState, rng: Random) {
        // Idempotent: the played game begins the expedition before showing the setup review (it
        // needs the stocks to display), and Engine.run begins it again. Without this guard every
        // startup thread was scheduled twice, and the player got "Earth has stopped answering"
        // and "The pharmacy has expired" twice each — a bug the harness could never see, because
        // the harness only ever calls run().
        if (s.hasFlag("expedition_begun")) return
        s.flags["expedition_begun"] = FlagVal(0)
        // The catastrophe is not flavour: the colony launches already leaning.
        s.cata?.let { c ->
            c.startPressures.forEach { (k, v) -> if (s.pressure.containsKey(k)) s.pressure[k] = v }
            c.startFlags.forEach { s.flags[it] = FlagVal(0) }
            if (c.earthPersistsYears in 1..99)
                s.threads.add(Thread(familyId = "earth_goes_quiet", dueMonth = c.earthPersistsYears * 12))
        }
        // Starting stores are a function of what was actually installed — no free lunch (A1).
        s.food = Physics.modifier(s, "foodStore") + s.build.adults * 6.0
        s.water = Physics.modifier(s, "waterStore") + 20.0
        s.spares = Physics.modifier(s, "sparesStore") + s.build.modules.size * 3.0
        s.medicine = Physics.modifier(s, "medStore") + s.build.adults * 1.2
        s.deltaV = Physics.modifier(s, "deltaV")
        s.cultivated = Physics.modifier(s, "growArea")
        s.crewNames = NAMES.shuffled(rng).toMutableList()
        Knowledge.seedEstimates(s, rng)
        // Pharmacy shelf life is a real 20-year-mission killer (see R2). Schedule it up front.
        s.threads.add(Thread(familyId = "pharmacy_expiry", dueMonth = (8 + rng.nextInt(6)) * 12))
    }

    fun maybeLandfall(s: WorldState, rng: Random) {
        if (s.phase == Phase.SPACE && s.year >= s.build.arrivalYear && !s.hasFlag("arrived"))
            s.flags["arrived"] = FlagVal(s.month)
        if (s.phase == Phase.SURFACE && !s.hasFlag("landed")) {
            s.flags["landed"] = FlagVal(s.month)
            // Landfall converts orbital kit into ground capability. How much depends on the planet:
            // a greenhouse is only as good as the water and light you can point at it.
            s.cultivated += Physics.modifier(s, "surfaceGrowArea") *
                    (0.25 + 0.5 * s.planet.water + 0.25 * s.planet.insolation.coerceAtMost(1.0))
            val revived = s.cryo * 0.97          // some pods always fail. Realism, and it hurts.
            s.adults += revived
            s.cryo = 0.0
        }
    }

    /** What a module actually does, in a phrase that fits "it ___" or "used to ___". */
    /**
     * `describe` returns a BARE INFINITIVE ("make 400 kW", "hold the spares", "mine water") and
     * `describeS` conjugates it ("makes 400 kW"). Both are needed because the corpus uses the slot
     * in two grammatical positions — "used to {subsystemDoes}" and "It {subsystemDoes}" — and until
     * now a single string served both, so EVERY module broke one of them. Modules that grant a
     * capability produced "It mine water"; every other module produced "used to makes 400 kW".
     * Four separate readers in one run stopped on this (playtest 05, D59/D60/D66/D67).
     *
     * Capability verbs are already bare, which is why bare is the canonical form.
     */
    fun describeS(mod: Module?): String {
        val bare = describe(mod)
        val first = bare.substringBefore(' ')
        val rest = if (bare.contains(' ')) " " + bare.substringAfter(' ') else ""
        val conj = when {
            first.endsWith("y") && first.length > 1 && first[first.length - 2] !in "aeiou" ->
                first.dropLast(1) + "ies"
            first.endsWith("s") || first.endsWith("sh") || first.endsWith("ch") ||
                first.endsWith("x") || first.endsWith("z") -> first + "es"
            else -> first + "s"
        }
        return conj + rest
    }

    fun describe(mod: Module?): String = when {
        mod == null -> "hold air"
        mod.grants.isNotEmpty() -> Content.corpus.capabilities
            .filter { mod.grants.contains(it.id) }
            .joinToString(", ") { it.verb.substringBefore(" (") }
        mod.powerGen > 0 -> "make " + mod.powerGen.toInt() + " kW of the colony's power"
        mod.modifiers.containsKey("growArea") || mod.modifiers.containsKey("surfaceGrowArea") ->
            "grow a share of the food"
        mod.modifiers.containsKey("shielding") -> "keep the radiation off"
        mod.modifiers.containsKey("foodStore") -> "hold the food stores"
        mod.modifiers.containsKey("sparesStore") -> "hold the spares"
        mod.modifiers.containsKey("waterStore") -> "hold the water"
        mod.modifiers.containsKey("medOut") -> "make what medicine there is"
        // The nuclear stage — 120 tonnes and the costliest module in the catalogue — fell through
        // to "holds pressure", and so did the counsellor. A module whose function the interface
        // cannot name is a module the player cannot reason about (A3, and D002's whole premise).
        mod.modifiers.containsKey("deltaV") ->
            "carry " + mod.modifiers["deltaV"]!!.toInt() + " km/s of spare fuel, which is what " +
                    "lets the expedition change its mind after it arrives"
        mod.modifiers.containsKey("fertility") -> "make people readier to have children"
        mod.modifiers.containsKey("durability") -> "make the hull harder to break"
        mod.modifiers.containsKey("shelterFor") ->
            "shelter " + mod.modifiers["shelterFor"]!!.toInt() + " people from radiation"
        else -> "hold air"
    }

    /**
     * NOBODY LIVES A HUNDRED YEARS. A name is drawn from the cohort ALIVE NOW.
     *
     * The pool was fixed at launch and drawn from for the whole century, so a playtest reached
     * year 62 and found the person asking about the birth rule was Osei — who had died in year 4,
     * on a screen that also carried the line *"The naming of the dead — each morning they read
     * the list aloud."* Turning the pool over every twenty-five years costs nothing and makes the
     * cast obey the demography that is already being simulated.
     */
    private fun someoneAliveNow(s: WorldState, rng: Random): String {
        val generation = s.year / 25
        val n = s.crewNames.size
        if (n == 0) return "the duty officer"
        // Deterministic per generation, so the same person can be named twice in a decade — which
        // is what a small colony is like — but never across two lifetimes.
        val offset = (generation * 7 + generation * generation * 3) % n
        val window = maxOf(4, n / 3)
        return s.crewNames[(offset + rng.nextInt(window)) % n]
    }

    fun bind(s: WorldState, e: GameEvent, rng: Random): Map<String, String> {
        val b = mutableMapOf<String, String>()
        for (slot in e.slots) when (slot) {
            "crew" -> b["crew"] = someoneAliveNow(s, rng)
            /**
             * A CROWD IS A FRACTION OF THIS COLONY, not a number somebody typed.
             *
             * "A petition of thirty names" arrived from eleven adults; "a crowd that cannot be
             * walked through" from fifteen. One reviewer supplied the argument for fixing it
             * rather than merely trimming it: *"'A petition of nine names' from eleven adults is
             * far more frightening than thirty from a phantom crowd."*
             *
             * `{few}` ~ a tenth, `{some}` ~ a quarter, `{many}` ~ half — floored at 2, 3 and 4 so
             * the fiction still works at the smallest colony in the game.
             */
            "crowd" -> {
                val ad = s.adults.coerceAtLeast(1.0)
                b["few"] = kotlin.math.max(2.0, ad * 0.10).roundToInt().toString()
                b["some"] = kotlin.math.max(3.0, ad * 0.25).roundToInt().toString()
                b["many"] = kotlin.math.max(4.0, ad * 0.50).roundToInt().toString()
                b["adults"] = ad.roundToInt().toString()
                b["souls"] = s.popTotal.roundToInt().toString()
            }
            "subsystem" -> {
                val cand = s.installed.filter { (s.moduleHealth[it] ?: 0.0) > 0.0 }
                b["subsystem"] = cand.randomOrNull(rng) ?: "hull"
                val mod = Content.module(b["subsystem"]!!)
                b["subsystemName"] = mod?.name ?: "the hull"
                // A NAME is not a FUNCTION. Cold readers could not weigh "leave it dead" against
                // "send someone outside to fix it" because nothing said what the thing did — three
                // separate events asked for real resources to save an unnamed capability.
                b["subsystemDoes"] = describe(mod)
                b["subsystemDoesNow"] = describeS(mod)
            }
            "cohort" -> b["cohort"] = listOf("the technicians", "the born-here", "the sleepers' kin",
                "the farm crews", "the deck hands").random(rng)
            "crop" -> b["crop"] = listOf("wheat", "potatoes", "soy", "algae", "kale").random(rng)
        }
        return b
    }
}
